import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { Eyebrow } from "./Shared";

const HERO_IMG =
  "https://images.unsplash.com/photo-1756477558468-b3e485757470?crop=entropy&cs=srgb&fm=jpg&q=85";

const LINE_VARIANTS = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.13, ease: [0.19, 1, 0.22, 1] },
  }),
};

const STATS = [
  { value: "20+ YRS", label: "In sports representation" },
  { value: "NFL · MLB", label: "Combat sports · Action sports" },
  { value: "2023", label: "Traditional division sold" },
  { value: "2026", label: "SMG Sports Management launch" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentFade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex min-h-screen flex-col overflow-hidden"
    >
      <motion.div
        className="absolute inset-0"
        style={{ scale: bgScale }}
        aria-hidden="true"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="img-blend h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/60 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-transparent to-ink/40" />
      </motion.div>
      <div className="hero-grid-bg absolute inset-0" aria-hidden="true" />
      <div
        className="absolute -right-40 top-1/4 h-[560px] w-[560px] rounded-full bg-volt/15 blur-[140px]"
        aria-hidden="true"
      />

      <motion.div
        style={{ opacity: contentFade }}
        className="relative mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-6 pb-10 pt-32 sm:px-12 sm:pt-36 lg:px-20 lg:pt-40"
      >
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-6 flex flex-wrap items-center gap-3"
          >
            <span className="h-2 w-2 rounded-full bg-volt" />
            <Eyebrow>SMG Sports Management Group International</Eyebrow>
          </motion.div>

          <h1 className="font-display text-[clamp(1.75rem,7.2vw,6.5rem)] uppercase leading-[1.02]">
            {[
              <>Pickleball Athlete</>,
              <>Representation.</>,
              <span className="text-volt">We Build</span>,
              <span className="text-volt">Athlete Brands.</span>,
            ].map((content, i) => (
              <div key={i} className="overflow-hidden py-[0.06em]">
                <motion.div
                  variants={LINE_VARIANTS}
                  custom={i}
                  initial="hidden"
                  animate="show"
                  className="will-change-transform"
                >
                  {content}
                </motion.div>
              </div>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
          >
            Pickleball-focused representation, sponsorship development, brand
            partnerships, media strategy and career management — the sports
            management system behind the next generation of athletes, powered
            by the full resources of SMG Media Group.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#apply"
              data-testid="hero-cta-athlete"
              className="group inline-flex items-center gap-2 rounded-full bg-volt px-8 py-4 font-display text-base uppercase tracking-wider text-ink transition-all hover:bg-volt-light hover:shadow-[0_0_40px_rgba(132,204,22,0.35)]"
            >
              Apply for Representation
              <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#partners"
              data-testid="hero-cta-brand"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-display text-base uppercase tracking-wider text-white transition-all hover:border-volt hover:text-volt"
            >
              Partner with an Athlete
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 1.25 }}
        className="relative bg-ink/60 backdrop-blur-sm"
        data-testid="hero-stats-strip"
      >
        <div className="spectrum-rule" aria-hidden="true" />
        <div className="mx-auto grid w-full max-w-7xl grid-cols-2 gap-x-6 gap-y-4 px-6 py-6 sm:px-12 md:grid-cols-4 lg:px-20">
          {STATS.map((s) => (
            <div key={s.value} data-testid="hero-stat">
              <p className="font-display text-xl uppercase text-volt sm:text-2xl">
                {s.value}
              </p>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400 sm:text-xs">
                {s.label}
              </p>
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute -top-16 left-1/2 -translate-x-1/2 text-slate-500">
          <ChevronDown className="h-5 w-5 animate-bounce" />
        </div>
      </motion.div>
    </section>
  );
}
